import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, TrendingUp, ArrowDownToLine } from 'lucide-react';
import DashboardLayout, { NavCard } from '../components/DashboardLayout';
import { usePerms } from '../permissions/PermissionsContext';

/**
 * MerchantPlatformFinance — iter-264c (Feb 2026)
 *
 * Top-level hub for the merchant platform's finance section. Per user
 * request the previous flat grid of 8 cards is replaced with 3 grouping
 * hubs: "إنشاء الفواتير", "الإيرادات", "السحب". Each hub owns the
 * deep-linked screens that used to live on this page directly.
 */
export default function MerchantPlatformFinance() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const hubs = [
    {
      id: 'invoices',
      titleAr: 'إنشاء الفواتير',
      titleEn: 'Create Invoices',
      descAr: 'إنشاء فواتير التجار العامة وفواتير الوكلاء',
      descEn: 'Merchant general invoices and agent invoices',
      icon: FileText,
      route: '/dashboard/finance/cloudgm/merchants/invoices',
      color: '#10b981',
      screenCode: 'FIN-CGM-MERCH-INV',
      testId: 'card-hub-invoices',
    },
    {
      id: 'revenues',
      titleAr: 'الإيرادات',
      titleEn: 'Revenues',
      descAr: 'إيرادات العمولة، مدفوعات الوكلاء، الرسوم الحكومية والمتاجر',
      descEn: 'Commissions, agent payments, gov fees and store revenues',
      icon: TrendingUp,
      route: '/dashboard/finance/cloudgm/merchants/revenues',
      color: '#d4af37',
      screenCode: 'FIN-CGM-MERCH-REV',
      testId: 'card-hub-revenues',
    },
    {
      id: 'withdrawal',
      titleAr: 'السحب',
      titleEn: 'Withdrawal',
      descAr: 'إدراج طلبات السحب اليدوية ومراجعة طلبات السحب',
      descEn: 'Insert manual withdrawals and review withdrawal requests',
      icon: ArrowDownToLine,
      route: '/dashboard/finance/cloudgm/merchants/withdrawal',
      color: '#3B9EDC',
      screenCode: 'FIN-CGM-MERCH-WD',
      testId: 'card-hub-withdrawal',
    },
  ];

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  return (
    <DashboardLayout
      title="منصة التجار (المالية)"
      titleEn="Merchant Platform (Finance)"
      subtitle="الإدارة المالية لمنصة التجار"
      subtitleEn="Financial management for merchant platform"
      screenCode="FIN-CGM-MERCH"
      backPath="/dashboard/finance/cloudgm"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                     path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',              path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)', path: null },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {visiblePages(hubs, 'route').map((item) => (
            <NavCard
              key={item.id}
              icon={item.icon}
              title={item.titleAr}
              titleEn={item.titleEn}
              description={item.descAr}
              descriptionEn={item.descEn}
              onClick={() => navigate(item.route)}
              color={item.color}
              screenCode={item.screenCode}
              testId={item.testId}
            />
          ))}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
